import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase, getStoredUser } from "../supabase";
import PostTime, { postNameColumn } from "../components/PostTime";

import LikeButton from "../components/LikeButton";
import { fetchMyEngagement, subscribeToMyEngagement } from "../utils/myEngagement";
import { subscribeToStatsPolling } from "../utils/statsPolling";
import DownloadButton from "../components/DownloadButton";
import SaveButton from "../components/SaveButton";
import Comments from "../components/Comments";
import ImageLightbox from "../components/ImageLightbox";
import TruncatedText from "../components/TruncatedText";
import FollowListModal from "../components/FollowListModal";
import ImageCarousel from "../components/ImageCarousel";
import ShareButton from "../components/ShareButton";
import ReportModal from "../components/ReportModal";
import LazyImage from "../components/LazyImage";
import PickerSelect from "../components/PickerSelect";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../components/Toast";
import { createNotification } from "../utils/notifications";
import { blockUser, unblockUser, checkBlockStatus } from "../utils/blocks";
import { muteUser, unmuteUser, checkMuteStatus } from "../utils/mutes";
import { friendlyError } from "../utils/errors";
import PostSkeleton from "../components/PostSkeleton";
import { likeDesign } from "../utils/likes";
import PostMenu from "../components/PostMenu";
import { alertDialog, confirmDialog, confirmLogin } from "../utils/dialog";
import { IoEllipsisVertical, IoPricetag, IoFlag } from "react-icons/io5";
import VerifiedBadge from "../components/VerifiedBadge";
import ReferralBadge from "../components/ReferralBadge";
import TagInfoModal from "../components/TagInfoModal";
import { PiChatCircleBold } from "react-icons/pi";

const PAGE_SIZE = 10;

// Newest first, oldest first, or most liked first (ties broken by newest, so a page
// never skips or repeats a post).
function orderPosts(query, sortBy) {
  if (sortBy === "oldest") return query.order("created_at", { ascending: true });
  if (sortBy === "likes") {
    return query.order("likes", { ascending: false }).order("created_at", { ascending: false });
  }
  return query.order("created_at", { ascending: false });
}

function PublicProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [userData, setUserData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const [designs, setDesigns] = useState([]);
  const [postsCount, setPostsCount] = useState(0);
  const [designsLoading, setDesignsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(getStoredUser);
  // Lets the merged profile-owner realtime channel below read the latest
  // viewer without needing currentUser in its own dependency array — that
  // channel is keyed to the PROFILE OWNER, not the viewer, and shouldn't
  // tear down and reconnect every time auth state resolves.
  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [tagModalId, setTagModalId] = useState(null);
  const [designReportId, setDesignReportId] = useState(null);
  const [openPostMenuId, setOpenPostMenuId] = useState(null);
  const postMenuRef = useRef(null);
  const [openComments, setOpenComments] = useState({});

  // Close a design card's ⋮ menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (postMenuRef.current && !postMenuRef.current.contains(e.target)) {
        setOpenPostMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The profile userId this page is currently showing posts for —
  // loadMorePosts reads this to tell whether userId changed out from under
  // an in-flight "load more" fetch (see loadMorePosts below).
  const activeUserIdRef = useRef(userId);

  const sentinelRef = useRef(null);
  const [postsOffset, setPostsOffset] = useState(0);
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | likes
  // loadMorePosts reads this to tell whether the sort changed while a "load more" was in flight.
  const activeSortRef = useRef(sortBy);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const loadingMorePostsRef = useRef(false);

  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followListType, setFollowListType] = useState(null); // null | "followers" | "following"
  const [followToast, setFollowToast] = useState("");
  const followPendingRef = useRef(new Set()); // profile ids a follow/unfollow is currently in flight for

  const [blockedByMe, setBlockedByMe] = useState(false);
  const [blockedMe, setBlockedMe] = useState(false);
  const [blockChecked, setBlockChecked] = useState(false);
  const [mutedByMe, setMutedByMe] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadUser() {
      setLoading(true);
      setNotFound(false);

      let { data, error } = await supabase
        .from("users")
        .select("id, username, avatar_url, bio, verified, show_followers, show_following, referral_count")
        .eq("id", userId)
        .single();

      // Each of these two extra columns landed in a separate migration —
      // referral_count is the newer of the two, so if only one is missing
      // (SQL run partway, or not yet at all) it's the one to drop first.
      // Falls all the way back to the original base columns so the profile
      // still loads even before any migration below this point has run.
      if (error?.code === "42703" || error?.code === "42501") {
        ({ data, error } = await supabase
          .from("users")
          .select("id, username, avatar_url, bio, verified, show_followers, show_following")
          .eq("id", userId)
          .single());
      }
      if (error?.code === "42703" || error?.code === "42501") {
        ({ data, error } = await supabase
          .from("users")
          .select("id, username, avatar_url, bio, verified")
          .eq("id", userId)
          .single());
      }

      // Navigating to another profile before this resolved (two fetches now
      // in flight) used to let whichever one finished last win — if that was
      // this now-stale one, the page would show one user's username/avatar/
      // bio while the URL (and everything else on the page) had already
      // moved on to a different profile.
      if (cancelled) return;

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setUserData(data);
      setLoading(false);
    }

    loadUser();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // userId changing (e.g. clicking through to another user's profile from a
  // comment or follower list) doesn't unmount this page, so without this a
  // lightbox or comments panel left open from the previous profile would
  // keep showing that profile's cached content after the page has moved on.
  useEffect(() => {
    setLightboxImage(null);
    setOpenComments({});
  }, [userId]);

  useEffect(() => {
    if (!userData) return;
    let cancelled = false;
    const targetId = userData.id;

    async function loadFollowData() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      const user = session?.user ?? null;

      if (cancelled) return;
      setCurrentUser(user);

      if (user && user.id === targetId) {
        navigate("/profile", { replace: true });
        return;
      }

      // All five of these only need user?.id/targetId, known up front — none
      // depends on another's result, so one Promise.all (instead of this
      // group followed by two more separately-awaited calls) saves 2 extra
      // network round-trips on every single profile-page load.
      const [{ count: followers }, { count: following }, followingCheck, status, muted] = await Promise.all([
        supabase
          .from("follows")
          .select("*", { count: "exact", head: true })
          .eq("following_id", targetId),
        supabase
          .from("follows")
          .select("*", { count: "exact", head: true })
          .eq("follower_id", targetId),
        user
          ? supabase
              .from("follows")
              .select("*")
              .eq("follower_id", user.id)
              .eq("following_id", targetId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        checkBlockStatus(user?.id, targetId),
        checkMuteStatus(user?.id, targetId),
      ]);

      // Navigating to another profile before this resolved used to let this
      // stale response overwrite the NEW profile's just-loaded follow
      // counts/status with the OLD profile's, since this closure still
      // pointed at whichever profile was current when it started.
      if (cancelled) return;

      setFollowersCount(followers || 0);
      setFollowingCount(following || 0);
      setIsFollowing(!!followingCheck.data);
      setBlockedByMe(status.blockedByMe);
      setBlockedMe(status.blockedMe);
      setBlockChecked(true);
      setMutedByMe(muted);
    }

    loadFollowData();

    return () => {
      cancelled = true;
    };
    // Only re-run when the profile itself changes, not on every realtime
    // field update (show_followers toggling, etc.) that also produces a new
    // userData object reference — those don't need a full follow-data reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // Close the "⋮" menu when clicking outside it.
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleToggleBlock() {
    if (!currentUser) {
      navigate("/login");
      return;
    }
    setMenuOpen(false);
    // Navigating to a different profile while this was still in flight used
    // to let its result apply to whatever profile is on screen now, instead
    // of the one it was actually about.
    const requestUserId = userData.id;

    if (blockedByMe) {
      const { error } = await unblockUser(currentUser.id, userData.id);
      if (requestUserId !== activeUserIdRef.current) return;
      if (!error) setBlockedByMe(false);
      else alertDialog(friendlyError(error, "Couldn't unblock. Try again."));
    } else {
      const ok = await confirmDialog({
        message: `Block @${userData.username}? Any follow between you will be removed, they won't be able to see your posts or follow you, and you won't see theirs.`,
        confirmText: "Block",
        danger: true
      });
      if (!ok) return;

      const { error } = await blockUser(currentUser.id, userData.id);
      if (requestUserId !== activeUserIdRef.current) return;
      if (!error) {
        setBlockedByMe(true);
        setIsFollowing(false);
      } else {
        alertDialog(friendlyError(error, "Couldn't block. Try again."));
      }
    }
  }

  async function handleToggleMute() {
    if (!currentUser) {
      navigate("/login");
      return;
    }
    setMenuOpen(false);
    const requestUserId = userData.id;

    if (mutedByMe) {
      const { error } = await unmuteUser(currentUser.id, userData.id);
      if (requestUserId !== activeUserIdRef.current) return;
      if (!error) setMutedByMe(false);
      else alertDialog(friendlyError(error, "Couldn't unmute. Try again."));
    } else {
      const { error } = await muteUser(currentUser.id, userData.id);
      if (requestUserId !== activeUserIdRef.current) return;
      if (!error) setMutedByMe(true);
      else alertDialog(friendlyError(error, "Couldn't mute. Try again."));
    }
  }

  // Live updates: the owner flipping "Show followers"/"Show following" (or
  // editing their profile), plus follows/unfollows made by ANYONE — merged
  // onto one channel instead of two, since both are keyed to this same
  // profile owner and share the same mount/unmount lifecycle. Reads the
  // viewer via currentUserRef rather than depending on currentUser directly,
  // so this doesn't tear down and reconnect every time auth state resolves
  // (currentUser and userData load independently — see loadFollowData above).
  useEffect(() => {
    if (!userData) return;

    const channel = supabase
      .channel(`public-profile-live-${userData.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "users", filter: `id=eq.${userData.id}` },
        payload => {
          // Postgres RLS is row-level, not column-level — this row being
          // readable at all means realtime's payload.new can carry EVERY
          // column on it, including ones no REST query in this app ever
          // asks for on someone else's profile (is_admin, banned, ...).
          // Spreading the whole payload the way the rest of this effect's
          // sibling handlers don't need to would put those into client
          // state for anyone with devtools open. Whitelisted to exactly the
          // columns the initial fetch above already selects.
          const { avatar_url, bio, verified, show_followers, show_following, username, referral_count } = payload.new;
          setUserData(prev =>
            prev ? { ...prev, avatar_url, bio, verified, show_followers, show_following, username, referral_count } : prev
          );
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "follows", filter: `following_id=eq.${userData.id}` },
        payload => {
          setFollowersCount(prev => prev + 1);
          if (currentUserRef.current && payload.new.follower_id === currentUserRef.current.id) {
            setIsFollowing(true);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "follows", filter: `following_id=eq.${userData.id}` },
        payload => {
          setFollowersCount(prev => Math.max(0, prev - 1));
          if (currentUserRef.current && payload.old.follower_id === currentUserRef.current.id) {
            setIsFollowing(false);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "follows", filter: `follower_id=eq.${userData.id}` },
        () => setFollowingCount(prev => prev + 1)
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "follows", filter: `follower_id=eq.${userData.id}` },
        () => setFollowingCount(prev => Math.max(0, prev - 1))
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  /* ================= GET DESIGNS (paginated, PAGE_SIZE at a time) ================= */
  useEffect(() => {
    let cancelled = false;
    activeUserIdRef.current = userId;
    activeSortRef.current = sortBy;

    async function loadDesigns() {
      setDesignsLoading(true);

      const { count } = await supabase
        .from("designs")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId);

      if (cancelled) return;
      setPostsCount(count || 0);

      const { data, error } = await orderPosts(
        supabase.from("designs").select("*").eq("user_id", userId),
        sortBy
      ).range(0, PAGE_SIZE - 1);

      // Navigating to another profile while this was still loading used to
      // let whichever request finished last overwrite the grid, even when
      // it was for the profile no longer showing in the header above it.
      if (cancelled) return;

      if (error) {
        console.log("DESIGNS ERROR:", error);
        setDesignsLoading(false);
        return;
      }

      let list = data || [];
      if (currentUser) {
        const { likedIds, savedIds } = await fetchMyEngagement(list.map(d => d.id), currentUser.id);
        if (cancelled) return;
        list = list.map(d => ({ ...d, _likedByMe: likedIds.has(d.id), _savedByMe: savedIds.has(d.id) }));
      }

      setDesigns(list);
      setPostsOffset(PAGE_SIZE);
      setHasMorePosts(list.length === PAGE_SIZE);
      setDesignsLoading(false);
    }

    loadDesigns();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sortBy]);

  // Load the next PAGE_SIZE posts (infinite scroll).
  async function loadMorePosts() {
    if (designsLoading || loadingMorePostsRef.current || !hasMorePosts) return;
    const requestUserId = userId;
    const requestSort = sortBy;
    loadingMorePostsRef.current = true;
    setLoadingMorePosts(true);

    const { data, error } = await orderPosts(
      supabase.from("designs").select("*").eq("user_id", userId),
      sortBy
    ).range(postsOffset, postsOffset + PAGE_SIZE - 1);

    // The profile changed (and loadDesigns already reset the grid) while
    // this page was still in flight — appending it now would tack the OLD
    // profile's posts onto the NEW profile's freshly-reset grid. Clearing
    // loadingMorePosts here too matters: left stuck true, every future
    // loadMorePosts() call on the new profile would return immediately (its
    // own guard checks loadingMorePosts), permanently breaking infinite
    // scroll until the page reloads.
    if (requestUserId !== activeUserIdRef.current || requestSort !== activeSortRef.current) {
      loadingMorePostsRef.current = false;
      setLoadingMorePosts(false);
      return;
    }

    if (error) {
      console.log("LOAD MORE POSTS ERROR:", error);
      loadingMorePostsRef.current = false;
      setLoadingMorePosts(false);
      return;
    }

    let newDesigns = data || [];
    if (currentUser) {
      const { likedIds, savedIds } = await fetchMyEngagement(newDesigns.map(d => d.id), currentUser.id);
      if (requestUserId !== activeUserIdRef.current || requestSort !== activeSortRef.current) {
        loadingMorePostsRef.current = false;
        setLoadingMorePosts(false);
        return;
      }
      newDesigns = newDesigns.map(d => ({ ...d, _likedByMe: likedIds.has(d.id), _savedByMe: savedIds.has(d.id) }));
    }

    setDesigns(prev => [...prev, ...newDesigns]);
    setPostsOffset(prev => prev + PAGE_SIZE);
    setHasMorePosts(newDesigns.length === PAGE_SIZE);
    loadingMorePostsRef.current = false;
    setLoadingMorePosts(false);
  }

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || designsLoading || !hasMorePosts) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMorePosts();
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designsLoading, postsOffset, hasMorePosts, loadingMorePosts, userId]);

  // Keeps the latest `designs` for the poll below to read without a stale
  // closure — it runs on its own timer, not in response to designs changing.
  const designsRef = useRef(designs);
  useEffect(() => {
    designsRef.current = designs;
  }, [designs]);

  // Refreshes likes/downloads/comment_count for whatever's currently loaded
  // on this page, every 30s — not a realtime listener, since a plain
  // "designs" UPDATE subscription has no way to scope itself to just the
  // posts on screen and would broadcast every design's change to every
  // connected client regardless (see utils/statsPolling.js). Whether *this
  // viewer* liked/saved each one is a separate, per-user concern — see the
  // myEngagement effect below.
  useEffect(() => {
    return subscribeToStatsPolling(
      () => designsRef.current.map(d => d.id),
      rows => {
        const byId = new Map(rows.map(r => [r.id, r]));
        setDesigns(prev =>
          prev.map(d => {
            const fresh = byId.get(d.id);
            return fresh
              ? { ...d, likes: fresh.likes, downloads: fresh.downloads, comment_count: fresh.comment_count }
              : d;
          })
        );
      }
    );
  }, []);

  // Live updates for this viewer's OWN likes/saves — e.g. liking something
  // from another tab or device signed into the same account. Filtered to
  // just this user's rows (see utils/myEngagement.js).
  useEffect(() => {
    if (!currentUser) return undefined;

    const setEngagement = (designId, fields) => {
      setDesigns(prev => prev.map(d => (d.id === designId ? { ...d, ...fields } : d)));
    };

    return subscribeToMyEngagement(currentUser.id, {
      onLike: id => setEngagement(id, { _likedByMe: true }),
      onUnlike: id => setEngagement(id, { _likedByMe: false }),
      onSave: id => setEngagement(id, { _savedByMe: true }),
      onUnsave: id => setEngagement(id, { _savedByMe: false })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  async function refreshCommentCount(designId) {
    const { count, error } = await supabase
      .from("comments")
      .select("id", { count: "exact", head: true })
      .eq("design_id", designId);

    if (error) {
      console.log("REFRESH COUNT ERROR:", error);
      return;
    }

    setDesigns(prev =>
      prev.map(d => (d.id === designId ? { ...d, comment_count: count ?? d.comment_count } : d))
    );
  }

  function toggleComments(id) {
    const isCurrentlyOpen = !!openComments[id];
    setOpenComments(prev => ({ ...prev, [id]: !isCurrentlyOpen }));
    if (isCurrentlyOpen) refreshCommentCount(id);
  }

  function requireLogin(action) {
    if (currentUser) {
      action();
      return;
    }
    confirmLogin(t("common.logInToContinue"), t("nav.login")).then(goLogin => {
      if (goLogin) navigate("/login");
    });
  }

  async function handleFollow() {
    if (!currentUser) return;

    // Without this, a fast double-tap fires two requests before the first
    // one's result has re-rendered the button — both read the same
    // isFollowing and both send the same INSERT (or DELETE). Followers/
    // following counts are a live COUNT(*) on this table, so a duplicate
    // row from that race would permanently inflate a public count. A Set
    // keyed by profile id, not a single id (or a plain boolean), so
    // following A, navigating to B and following B, then navigating BACK to
    // A and following it again before the first A-request has resolved,
    // doesn't let that first request's cleanup clear the SECOND A-request's
    // still-in-flight guard out from under it.
    const requestUserId = userData.id;
    if (followPendingRef.current.has(requestUserId)) return;
    followPendingRef.current.add(requestUserId);

    const wasFollowing = isFollowing;

    // followersCount is NOT touched optimistically here — the realtime
    // subscription above already increments/decrements it for every
    // follows INSERT/DELETE on this profile, including this viewer's own
    // (Supabase echoes a change back to the client that made it). Bumping
    // it here too double-counted every single follow/unfollow.
    try {
      if (wasFollowing) {
        setIsFollowing(false);

        const { error } = await supabase
          .from("follows")
          .delete()
          .eq("follower_id", currentUser.id)
          .eq("following_id", userData.id);

        if (error) {
          console.log("UNFOLLOW ERROR:", error);
          if (requestUserId === activeUserIdRef.current) {
            setIsFollowing(true);
            setFollowToast("Couldn't unfollow. Try again.");
          }
        }
      } else {
        setIsFollowing(true);

        const { error } = await supabase.from("follows").insert({
          follower_id: currentUser.id,
          following_id: userData.id
        });

        if (error) {
          console.log("FOLLOW ERROR:", error);
          if (requestUserId === activeUserIdRef.current) {
            setIsFollowing(false);
            setFollowToast("Couldn't follow. Try again.");
          }
          return;
        }

        createNotification({ userId: userData.id, actorId: currentUser.id, type: "follow" });
      }
    } finally {
      followPendingRef.current.delete(requestUserId);
    }
  }

  if (loading) {
    return <p style={mutedText}>Loading…</p>;
  }

  if (notFound || !userData) {
    return (
      <div style={{ padding: 30 }}>
        <h2 style={{ color: "var(--text-heading)" }}>User not found</h2>
      </div>
    );
  }

  if (blockChecked && blockedMe) {
    return (
      <div style={{ padding: 30, textAlign: "center" }}>
        <h2 style={{ color: "var(--text-heading)" }}>This profile isn't available</h2>
      </div>
    );
  }

  return (
    <div className="profile-page" style={page}>
      <style>{`
        @media (max-width: 640px) {
          .profile-page { padding: 4px !important; }
          .profile-header { padding: 16px !important; }
          .profile-header-row { gap: 16px !important; }
          .profile-avatar { width: 72px !important; height: 72px !important; }
          .profile-stats-row { gap: 18px !important; }
          .profile-stats-row .profile-stat-value { font-size: 18px !important; }
        }
        @media (min-width: 641px) {
          .public-profile-grid {
            grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)) !important;
          }
        }
      `}</style>

      {/* ===== HEADER (matches Profile.jsx's own profile layout) ===== */}
      <div className="profile-header" style={header}>
       <div className="profile-header-row" style={topRow}>
        <div style={avatarWrap}>
          <img
            src={userData.avatar_url || "/default-avatar.svg"}
            alt={userData.username}
            className="profile-avatar"
            style={{ ...avatar, cursor: "pointer" }}
            onClick={() => userData.avatar_url && setLightboxImage({ images: [userData.avatar_url], index: 0 })}
          />
        </div>

        <div style={headerRight}>
          <div style={usernameRow}>
            <h2 style={usernameHeading}>
              <span style={usernameText}>@{userData.username}</span>
              {userData.verified && <VerifiedBadge size={20} />}
              <ReferralBadge count={userData.referral_count} size={16} />
            </h2>
          </div>

          <div className="profile-stats-row" style={statsRow}>
            <Stat label="Posts" value={postsCount} />
            <Stat
              label="Followers"
              value={followersCount}
              onClick={userData.show_followers === false ? null : () => setFollowListType("followers")}
            />
            <Stat
              label="Following"
              value={followingCount}
              onClick={userData.show_following === false ? null : () => setFollowListType("following")}
            />
          </div>
          {(userData.show_followers === false || userData.show_following === false) && (
            <p style={privateListText}>
              {userData.show_followers === false && userData.show_following === false
                ? "Follow lists are private"
                : userData.show_followers === false
                ? "Followers list is private"
                : "Following list is private"}
            </p>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {currentUser && currentUser.id !== userData.id && !blockedByMe && (
              <button
                onClick={handleFollow}
                style={isFollowing ? unfollowBtn : followBtn}
              >
                {isFollowing ? "Unfollow" : "Follow"}
              </button>
            )}
            <ShareButton
              url={`${window.location.origin}/u/${userData.id}`}
              text={`Check out @${userData.username}'s designs on GatherYou`}
            />

            {(!currentUser || currentUser.id !== userData.id) && (
            <div style={{ position: "relative" }} ref={menuRef}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                style={moreBtn}
                aria-label="More options"
              >
                <IoEllipsisVertical />
              </button>

              {menuOpen && (
                <div style={moreMenu}>
                  <div style={moreMenuItem} onClick={handleToggleBlock}>
                    {blockedByMe ? "Unblock user" : "Block user"}
                  </div>
                  {!blockedByMe && (
                    <div style={moreMenuItem} onClick={handleToggleMute}>
                      {mutedByMe ? "Unmute user" : "Mute user"}
                    </div>
                  )}
                  <div
                    style={{ ...moreMenuItem, borderBottom: "none", color: "var(--accent-danger)" }}
                    onClick={() => {
                      setMenuOpen(false);
                      if (!currentUser) {
                        navigate("/login");
                        return;
                      }
                      setReportOpen(true);
                    }}
                  >
                    Report user
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          {blockedByMe && (
            <p style={privateListText}>You've blocked this account.</p>
          )}
        </div>
       </div>

        {userData.bio && <p style={bioFullWidth}>{userData.bio}</p>}
      </div>

      {/* ===== POSTS: heading and sort ===== */}
      {!blockedByMe && (
        <>
          <hr style={divider} />
          <div style={uploadsHeaderRow}>
            <h3 style={uploadsHeading}>{t("profile.posts")}</h3>
            {/* The app's own list instead of the phone's native selection popup. */}
            <PickerSelect
              label={t("profile.sortPosts")}
              value={sortBy}
              onChange={setSortBy}
              style={sortSelect}
              options={[
                { value: "newest", label: t("profile.sortNewest") },
                { value: "oldest", label: t("profile.sortOldest") },
                { value: "likes", label: t("profile.sortLikes") }
              ]}
            />
          </div>
        </>
      )}

      {/* ===== GRID ===== */}
      {blockedByMe ? null : designsLoading ? (
        <PostSkeleton />
      ) : (
      <div className="public-profile-grid" style={grid}>
        {designs.length === 0 && <p style={mutedText}>No designs yet.</p>}

        {designs.map(d => {
          const commentsOpen = !!openComments[d.id];
          const commentCount = d.comment_count || 0;

          function updateDesign(fields) {
            setDesigns(prev =>
              prev.map(x => (x.id === d.id ? { ...x, ...fields } : x))
            );
          }

          return (
            <div key={d.id} style={card}>
              <div style={cardHeader}>
                <LazyImage
                  src={userData.avatar_url || "/default-avatar.svg"}
                  alt={`${userData.username || "User"}'s avatar`}
                  style={{ ...cardAvatar, flexShrink: 0 }}
                />
                <span style={{ ...postNameColumn, flex: 1 }}>
                  <span style={usernameSpan}>@{userData.username}</span>
                  <PostTime date={d.created_at} />
                </span>
                <div
                  style={{ position: "relative", marginLeft: "auto", flexShrink: 0 }}
                  ref={openPostMenuId === d.id ? postMenuRef : null}
                >
                  <button
                    type="button"
                    style={moreBtn}
                    onClick={() => setOpenPostMenuId(openPostMenuId === d.id ? null : d.id)}
                    aria-label="More options"
                  >
                    <IoEllipsisVertical />
                  </button>
                  {openPostMenuId === d.id && (
                    <PostMenu
                      items={[
                        { icon: <IoPricetag />, label: "Tag info", onClick: () => { setOpenPostMenuId(null); setTagModalId(d.id); } },
                        ...(currentUser?.id
                          ? [{ icon: <IoFlag />, label: "Report", onClick: () => { setOpenPostMenuId(null); setDesignReportId(d.id); } }]
                          : [])
                      ]}
                    />
                  )}
                </div>
              </div>

              <ImageCarousel
                images={d.images?.length > 0 ? d.images : [d.image_url]}
                alt={d.title}
                imgStyle={{ ...image, cursor: "zoom-in" }}
                onImageClick={setLightboxImage}
                onDoubleTap={() =>
                  likeDesign({ designId: d.id, user: currentUser, designOwnerId: d.user_id, onUpdate: updateDesign, likeOnly: true })
                }
              />

              <div style={content}>
                <div style={actions}>
                  <LikeButton
                    designId={d.id}
                    likes={d.likes}
                    liked={d._likedByMe}
                    user={currentUser}
                    designOwnerId={d.user_id}
                    onUpdate={updateDesign}
                  />

                  <button
                    onClick={() => requireLogin(() => toggleComments(d.id))}
                    style={commentBadgeBtn}
                    aria-label="Toggle comments"
                  >
                    <PiChatCircleBold size={27} /> <span style={commentCountText}>{commentCount}</span>
                  </button>

                  <DownloadButton
                    fileURL={d.design_url}
                    files={d.design_files}
                    designId={d.id}
                    downloads={d.downloads}
                    designOwnerId={d.user_id}
                    user={currentUser}
                    onUpdate={updateDesign}
                  />

                  <ShareButton designId={d.id} />

                  <span style={{ display: "inline-flex", marginLeft: "auto" }}>
                    <SaveButton
                      designId={d.id}
                      saved={d._savedByMe}
                      user={currentUser}
                      onUpdate={updateDesign}
                    />
                  </span>
                </div>

                <p style={caption}><TruncatedText text={d.title} /></p>
                {d.description && (
                  <p style={{ margin: "2px 0", color: "var(--text-body)", fontSize: "14px" }}>
                    {d.description}
                  </p>
                )}

                {commentsOpen && (
                  <Comments designId={d.id} onClose={() => toggleComments(d.id)} designOwnerId={d.user_id} />
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {!blockedByMe && !designsLoading && hasMorePosts && <div ref={sentinelRef} style={{ height: 1 }} />}
      {!blockedByMe && loadingMorePosts && <p style={mutedText}>Loading more…</p>}

      {reportOpen && (
        <ReportModal
          reporterId={currentUser?.id}
          targetType="user"
          targetId={userData.id}
          onClose={() => setReportOpen(false)}
        />
      )}

      {designReportId && (
        <ReportModal
          reporterId={currentUser?.id}
          targetType="design"
          targetId={designReportId}
          onClose={() => setDesignReportId(null)}
        />
      )}

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          initialIndex={lightboxImage.index}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {tagModalId && (
        <TagInfoModal designId={tagModalId} onClose={() => setTagModalId(null)} />
      )}

      {followListType && (
        <FollowListModal
          userId={userData.id}
          type={followListType}
          onClose={() => setFollowListType(null)}
        />
      )}

      {followToast && <Toast text={followToast} onClose={() => setFollowToast("")} />}
    </div>
  );
}

export default PublicProfile;

/* ================= STYLES (Virsa palette, matches Home/Trending/Saved) ================= */

const page = {
  padding: "20px 20px calc(90px + env(safe-area-inset-bottom))",
  maxWidth: 900,
  margin: "0 auto",
  background: "var(--bg-page)",
  minHeight: "100vh"
};

const mutedText = { padding: 20, fontSize: 14, color: "var(--text-muted)" };
const privateListText = { fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0" };

const header = {
  marginBottom: 20,
  background: "var(--bg-surface)",
  border: "1px solid var(--border-medium)",
  borderRadius: 18,
  padding: 24,
  boxShadow: "0 12px 30px rgba(74, 34, 24, 0.10)"
};

const topRow = {
  display: "flex",
  gap: 32,
  alignItems: "flex-start"
};

const avatarWrap = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  flexShrink: 0
};

const avatar = {
  width: 96,
  height: 96,
  borderRadius: "50%",
  objectFit: "cover",
  border: "3px solid var(--bg-soft)"
};

const headerRight = { flex: 1, minWidth: 0 };

const usernameRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
};

const statsRow = {
  display: "flex",
  alignItems: "center",
  gap: 36,
  margin: "16px 0 0"
};

const statItem = { textAlign: "center" };
const statValue = { fontSize: 22, fontWeight: 700, color: "var(--text-heading)", display: "block" };
const statLabel = { fontSize: 13.5, color: "var(--text-secondary)", marginTop: 2 };

function Stat({ label, value, onClick }) {
  return (
    <div
      style={{ ...statItem, cursor: onClick ? "pointer" : "default" }}
      onClick={onClick || undefined}
    >
      <strong className="profile-stat-value" style={statValue}>{value}</strong>
      <div style={statLabel}>{label}</div>
    </div>
  );
}

const usernameHeading = {
  margin: "0 0 4px",
  color: "var(--text-primary)",
  fontWeight: 700,
  fontSize: 19,
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0
};

const usernameText = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0
};
const bioFullWidth = {
  fontSize: 14,
  color: "var(--text-heading)",
  fontWeight: 600,
  lineHeight: 1.4,
  margin: "14px 0 0",
  whiteSpace: "pre-wrap"
};
const followBtn = {
  padding: "9px 18px",
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 13.5
};

const unfollowBtn = {
  ...followBtn,
  background: "none",
  border: "1.5px solid var(--border-input)",
  color: "var(--text-secondary)"
};

const moreBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 34,
  height: 34,
  border: "none",
  background: "none",
  color: "var(--text-primary)",
  fontSize: 22,
  cursor: "pointer"
};

const moreMenu = {
  position: "absolute",
  top: 38,
  right: 0,
  background: "var(--bg-surface)",
  border: "1px solid var(--border-medium)",
  borderRadius: 10,
  boxShadow: "0 8px 20px rgba(74,34,24,0.15)",
  overflow: "hidden",
  zIndex: 10,
  minWidth: 160
};

const moreMenuItem = {
  padding: "10px 14px",
  cursor: "pointer",
  fontSize: 13.5,
  color: "var(--text-heading)",
  borderBottom: "1px solid var(--border-soft)",
  whiteSpace: "nowrap"
};

const divider = { border: "none", borderTop: "1px solid var(--border-medium)", margin: "6px 0 12px" };

const uploadsHeaderRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 8
};

const uploadsHeading = {
  margin: 0,
  fontSize: 20,
  fontWeight: 800,
  color: "var(--text-heading)"
};

const sortSelect = {
  width: "auto",
  padding: "5px 12px",
  fontSize: 12.5,
  fontWeight: 600,
  borderRadius: 999,
  border: "1px solid var(--border-soft)",
  background: "var(--bg-soft)",
  color: "var(--text-secondary)",
  marginBottom: 0
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  gap: "10px 18px"
};

const card = {
  background: "var(--bg-surface)",
  borderRadius: 14,
  overflow: "hidden",
  border: "1px solid var(--border-soft)",
  boxShadow: "0 4px 14px rgba(59,10,20,0.08)"
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  gap: 24,
  padding: "4px 14px"
};

const cardAvatar = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)"
};

const usernameSpan = {
  fontWeight: 700,
  fontSize: 18,
  color: "var(--text-primary)"
};

const image = {
  width: "100%",
  height: "auto",
  display: "block"
};

const content = { padding: "12px 12px 4px" };

const caption = {
  margin: "0 0 4px",
  fontSize: 13.5,
  color: "var(--text-secondary)",
  whiteSpace: "pre-wrap"
};


const actions = {
  display: "flex",
  alignItems: "center",
  gap: 2
};

const commentBadgeBtn = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "none",
  border: "none",
  borderRadius: 20,
  height: 40,
  padding: "0 6px",
  fontSize: 14,
  fontWeight: 700,
  color: "var(--text-primary)",
  cursor: "pointer"
};

const commentCountText = {
  fontWeight: "bold",
  fontSize: 13,
  color: "var(--text-primary)"
};
